import { LockIcon } from "@phosphor-icons/react/dist/csr/Lock";
import { useFormikContext } from "formik";
import { FormikCheckboxWithLabel } from "@volca/ui/components/ui/checkbox-with-label";
import { Label } from "@volca/ui/components/ui/label";
import { MoneyInput } from "@volca/ui/components/ui/money-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { getCustomerNameAndPhone } from "@/customer/utils";
import { IReferral } from "@/referrals/referral";
import type { PayoutFormValues } from "./schema";

interface PayoutFieldsProps {
  referral: IReferral | null;
  requiresDealSize: boolean;
  maxPayout: number | null | undefined;
}

export const PayoutFields = ({
  referral,
  requiresDealSize,
  maxPayout,
}: PayoutFieldsProps) => {
  const { errors, touched, values, getFieldProps, setFieldValue } =
    useFormikContext<PayoutFormValues>();

  return (
    <Stack align="start" className="w-full" gap={6}>
      <Stack align="start" gap={2}>
        <Text variant="label" tone="muted">
          Paying out to
        </Text>
        <Text variant="title">
          {getCustomerNameAndPhone(referral?.advocate)}
        </Text>
      </Stack>

      {requiresDealSize && (
        <MoneyInput
          name="deal_size"
          label="Deal size"
          value={values.deal_size}
          onChange={(value) => setFieldValue("deal_size", value)}
          error={touched.deal_size ? errors.deal_size : undefined}
          required
          fullWidth
          autoFocus
        />
      )}

      <MoneyInput
        name="amount"
        label="Amount"
        value={values.amount}
        onChange={(value) => setFieldValue("amount", value)}
        error={touched.amount && values.amount ? errors.amount : undefined}
        info={maxPayout ? `The advertised maximum payout is $${maxPayout}` : ""}
        required
        autoFocus={!requiresDealSize}
        fullWidth
      />

      <Stack gap={2} className="w-full">
        <Label htmlFor="payout-customer-message">Customer message</Label>
        <Textarea
          id="payout-customer-message"
          className="min-h-32"
          placeholder="Send a message to the customer"
          {...getFieldProps("customer_message")}
        />
      </Stack>
      <Stack gap={2} className="w-full">
        <Label htmlFor="payout-notes">Notes</Label>
        <Textarea
          id="payout-notes"
          className="min-h-32"
          placeholder="Enter any internal notes here"
          {...getFieldProps("notes")}
        />
      </Stack>
      <Stack direction="row" align="center" gap={2} className="-mt-4">
        <LockIcon className="h-4 w-4 shrink-0 text-subtle" />
        <Text variant="caption" tone="muted">
          Your notes are only visible internally. They will not be shared with
          the customer.
        </Text>
      </Stack>

      <FormikCheckboxWithLabel
        id="confirmPayout"
        label="I understand that I am sending funds from the company account to a customer via SMS.*"
        checked={values.confirm_payout}
        onCheckedChange={(checked) => setFieldValue("confirm_payout", checked)}
      />

      <FormikCheckboxWithLabel
        id="finalPayout"
        label="This is the final payout for this referral"
        checked={values.final_payout}
        onCheckedChange={(checked) => setFieldValue("final_payout", checked)}
      />
    </Stack>
  );
};
